import { type BadgeOrderImageSource } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import {
  SHIFT_DISCOVERY_SHIFT_CONFIRM_BOOKING_MODAL_FULL_PATH,
  SHIFT_DISCOVERY_SHIFT_MODAL_FULL_PATH,
} from "@src/appV2/redesign/ShiftDiscovery/paths";
import { SHIFT_DETAILS_FULL_PATH } from "@src/appV2/redesign/WorkerShifts/path";
import { useCallback, useMemo, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { WorkBadgeReviewPolicySource } from "../components/Policy/types";

export enum WorkBadgeReviewPolicyStep {
  REVIEW_POLICY = "review_policy",
  SELECT_HEADSHOT = "select_headshot",
  BADGE_READY = "badge_ready",
}

interface UseWorkBadgeReviewPolicyResult {
  step: WorkBadgeReviewPolicyStep;
  setStep: (step: WorkBadgeReviewPolicyStep) => void;
  workerHeadshotBase64: string | undefined;
  setWorkerHeadshotBase64: (value: string | undefined) => void;
  imageSource: BadgeOrderImageSource;
  setImageSource: (value: BadgeOrderImageSource) => void;
  qualificationName: string | undefined;
  handleClose: () => void;
  primaryButtonText: string;
  source: WorkBadgeReviewPolicySource | undefined;
}

export function useWorkBadgeReviewPolicy(): UseWorkBadgeReviewPolicyResult {
  const history = useHistory();
  const searchParams = useSearchParams<{
    source?: WorkBadgeReviewPolicySource;
    qualificationName?: string;
    shiftId?: string;
    step?: WorkBadgeReviewPolicyStep;
    fromBadgeIntro?: string;
  }>();

  const { source, qualificationName, shiftId, step: initialStep, fromBadgeIntro } = searchParams;

  const [step, setStep] = useState<WorkBadgeReviewPolicyStep>(
    initialStep ?? WorkBadgeReviewPolicyStep.REVIEW_POLICY
  );

  const [workerHeadshotBase64, setWorkerHeadshotBase64] = useState<string | undefined>();
  const [imageSource, setImageSource] = useState<BadgeOrderImageSource>("contractor-badge-picture");

  const closePath = useMemo(() => {
    if (isDefined(shiftId)) {
      if (source === WorkBadgeReviewPolicySource.SHIFT_DETAILS) {
        return generatePath(SHIFT_DETAILS_FULL_PATH, { shiftId });
      }
      if (source === WorkBadgeReviewPolicySource.SHIFT_DISCOVERY) {
        if (fromBadgeIntro === "true") {
          return `${generatePath(SHIFT_DISCOVERY_SHIFT_CONFIRM_BOOKING_MODAL_FULL_PATH, { shiftId })}?showBadgeIntro=true`;
        }

        return generatePath(SHIFT_DISCOVERY_SHIFT_MODAL_FULL_PATH, { shiftId });
      }
    }

    return undefined;
  }, [shiftId, source, fromBadgeIntro]);

  const handleClose = useCallback(() => {
    if (source === WorkBadgeReviewPolicySource.BADGE_VERIFICATION) {
      history.goBack();
      return;
    }

    if (isDefined(closePath)) {
      history.replace(closePath);
      return;
    }

    history.goBack();
  }, [history, closePath, source]);

  const primaryButtonText = useMemo(() => {
    if (
      source === WorkBadgeReviewPolicySource.SHIFT_DETAILS ||
      source === WorkBadgeReviewPolicySource.BADGE_VERIFICATION
    ) {
      return "Go back to Bookings";
    }

    return "Go back to Shifts";
  }, [source]);

  return {
    step,
    setStep,
    workerHeadshotBase64,
    setWorkerHeadshotBase64,
    imageSource,
    setImageSource,
    qualificationName,
    handleClose,
    primaryButtonText,
    source,
  };
}
